import { Link } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type { HTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

interface Props {
  children: ReactNode;
  rootClassname?: HTMLAttributes<HTMLDivElement>["className"];
  contentClassName?: HTMLAttributes<HTMLDivElement>["className"];
  title: string;
  action?: {
    label: string;
    link: string;
  };
  icon?: LucideIcon | string;
  headerRight?: ReactNode;
  scrollable?: boolean;
}

const DashboardCardIcon = ({
  icon,
  title,
}: {
  icon: NonNullable<Props["icon"]>;
  title: string;
}) => {
  if (typeof icon === "string") {
    return (
      <div className="p-1 rounded-xs bg-primary-muted">
        <img
          src={icon}
          alt={title}
          draggable={false}
          className="size-8 object-contain"
        />
      </div>
    );
  }
  const Icon = icon;
  return <Icon className="text-primary" />;
};

export const DashboardCard = (props: Props) => {
  return (
    <Card className={`min-h-96 h-full flex flex-col ${props.rootClassname}`}>
      <CardHeader className="flex flex-row flex-wrap justify-between items-center py-3.5 gap-y-2">
        <CardTitle className="text-base! md:text-md! flex gap-2 items-center">
          {props.icon && (
            <DashboardCardIcon icon={props.icon} title={props.title} />
          )}
          {props.title}
        </CardTitle>
        {props.headerRight ??
          (props.action && (
            <Link
              to={props.action.link}
              className="text-primary hover:underline text-sm font-semibold"
            >
              {props.action.label}
            </Link>
          ))}
      </CardHeader>
      <Separator />
      <CardContent
        className={`pt-4 flex-1 shadow-inner ${(props.scrollable ?? true) ? "overflow-y-auto" : "overflow-hidden"} ${props.contentClassName ?? ""}`}
      >
        {props.children}
      </CardContent>
    </Card>
  );
};
